import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/postal_sending_options';

const PRINT_COLORS = ['BW', 'COLOR'] as const;
const PRINT_MODES = ['1SIDE', '2SIDE'] as const;

export const PostalSendingOptionsSchema = z.object({
  printColor: z.enum(PRINT_COLORS),
  printMode: z.enum(PRINT_MODES),
});

export type PostalSendingOptions = z.infer<typeof PostalSendingOptionsSchema>;

export async function loadPostalSendingOptionsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = PostalSendingOptionsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'postalSendingOptions');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadPostalSendingOptions(options: QueryOptions<PostalSendingOptions> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<PostalSendingOptions, ApiError>({
    queryKey: ['postalSendingOptions'],
    queryFn: () => loadPostalSendingOptionsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<PostalSendingOptions, ApiError, 'postalSendingOptions'>(
    'postalSendingOptions',
    queryResult,
  );
}
